import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accountsByEntityQueryOptions,
  accountsByEntityQueryKey,
  organizationsQueryOptions,
  rolesQueryOptions,
  sitesQueryOptions,
  studiesQueryOptions,
  updateAccountRolesMutateOptions,
} from '../../queryOptions';
import type { UpdateAccountRolesVariables } from '../../queryOptions';
import RoleAssignmentsTable from './roleAssignmentsComponents/RoleAssignmentsTable';
import {
  Text,
  Tabs,
  VStack,
  Card,
  DropdownList,
  DropdownButton,
  HStack,
} from '@adc/parallax-component-library';
import { EntityType } from 'adc-sirius-module-common';
import { useAppSelector } from '../../reducers/hooks';
import i18n from '../../utilities/i18n';
import type { AccountWithRoleAssignments } from '../../models';

enum TabIndex {
  Organization = 0,
  Study = 1,
  Site = 2,
}

const getEntityTypeForTab = (tabIndex: TabIndex): EntityType => {
  switch (tabIndex) {
    case TabIndex.Study:
      return EntityType.Study;
    case TabIndex.Site:
      return EntityType.Site;
    default:
      return EntityType.Organization;
  }
};

type TabConfigItem = {
  key: string;
  headerAction: React.ReactElement | undefined;
};

/**
 * View for displaying user accounts and roles organized by context
 * @returns {React.JSX.Element}
 */
export default function RoleAssignments() {
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    TabIndex.Organization,
  );

  const [selectedOrganizationIndex, setSelectedOrganizationIndex] = useState(0);
  const [selectedStudyIndex, setSelectedStudyIndex] = useState(0);
  const [selectedSiteIndex, setSelectedSiteIndex] = useState(0);

  const queryClient = useQueryClient();

  const { currentOrgKey: reduxOrgKey } = useAppSelector(
    (state) => state.organizations,
  );

  const tabs = [
    {
      'aria-label': 'Organization',
      label: i18n.t('Page_RoleAssignments.tabs.organization'),
    },
    {
      'aria-label': 'Study',
      label: i18n.t('Page_RoleAssignments.tabs.study'),
    },
    {
      'aria-label': 'Site',
      label: i18n.t('Page_RoleAssignments.tabs.site'),
    },
  ];

  const organizations = useQuery(organizationsQueryOptions());

  const studies = useQuery(
    studiesQueryOptions(
      {
        organizationKey:
          organizations.data?.[selectedOrganizationIndex]?.key ?? '',
      },
      {
        enabled:
          !!organizations.data?.[selectedOrganizationIndex]?.key &&
          (selectedTabIndex === TabIndex.Study ||
            selectedTabIndex === TabIndex.Site),
      },
    ),
  );

  const sites = useQuery(
    sitesQueryOptions(
      { studyKey: studies.data?.[selectedStudyIndex]?.key ?? '' },
      {
        enabled:
          !!studies.data?.[selectedStudyIndex]?.key &&
          selectedTabIndex === TabIndex.Site,
      },
    ),
  );

  const currentOrganization = organizations.data?.[selectedOrganizationIndex];
  const currentOrgKey = currentOrganization?.key ?? '';

  const currentStudy = studies.data?.[selectedStudyIndex];
  const currentStudyKey = currentStudy?.key ?? '';

  const currentSite = sites.data?.[selectedSiteIndex];
  const currentSiteKey = currentSite?.key ?? '';

  const getAccountsByEntityOptions = (
    entityKey: string,
    tabIndex: TabIndex,
  ) => {
    const entityType = getEntityTypeForTab(tabIndex);

    return accountsByEntityQueryOptions(entityKey, entityType, {
      enabled: selectedTabIndex === tabIndex,
    });
  };

  const organizationAccountsQuery = useQuery(
    getAccountsByEntityOptions(currentOrgKey, TabIndex.Organization),
  );

  const studyAccountsQuery = useQuery(
    getAccountsByEntityOptions(currentStudyKey, TabIndex.Study),
  );

  const siteAccountsQuery = useQuery(
    getAccountsByEntityOptions(currentSiteKey, TabIndex.Site),
  );

  const roles = useQuery(
    rolesQueryOptions({
      entityType: getEntityTypeForTab(selectedTabIndex),
    }),
  );

  // on success, refetch assignments to keep table state aligned
  const updateRolesMutation = useMutation(
    updateAccountRolesMutateOptions({
      onSuccess: (
        _updatedAccounts: AccountWithRoleAssignments[],
        mutationVariables: UpdateAccountRolesVariables,
      ) => {
        const entityKey = mutationVariables.updateRequest.sourceEntity;

        if (!entityKey) {
          return;
        }

        const entityQueryKey = accountsByEntityQueryKey(entityKey);

        queryClient.invalidateQueries({ queryKey: entityQueryKey });
      },
    }),
  );

  const activeEntityScope = (() => {
    switch (selectedTabIndex) {
      case TabIndex.Study:
        return {
          accountsQuery: studyAccountsQuery,
          entityKey: currentStudyKey,
        };
      case TabIndex.Site:
        return {
          accountsQuery: siteAccountsQuery,
          entityKey: currentSiteKey,
        };
      default:
        return {
          accountsQuery: organizationAccountsQuery,
          entityKey: currentOrgKey,
        };
    }
  })();

  const accountsWithRoleAssignments = activeEntityScope.accountsQuery;

  const isLoadingAccounts =
    !!activeEntityScope.entityKey && activeEntityScope.accountsQuery.isPending;

  const isDisabledDueToLoading =
    isLoadingAccounts || roles.isPending || updateRolesMutation.isPending;

  useEffect(() => {
    if (organizations.data && reduxOrgKey) {
      const index = organizations.data.findIndex(
        (org) => org.key === reduxOrgKey,
      );

      if (index !== -1) {
        setSelectedOrganizationIndex(index);
      }
    }
  }, [organizations.data, reduxOrgKey]);

  useEffect(() => {
    setSelectedStudyIndex(0);
    setSelectedSiteIndex(0);
  }, [selectedOrganizationIndex]);

  const onSaveRoleChanges = async (
    account: AccountWithRoleAssignments,
    addRoleKeys: string[],
    removeRoleKeys: string[],
  ): Promise<void> => {
    if (!activeEntityScope.entityKey) {
      return;
    }

    await updateRolesMutation.mutateAsync({
      updateRequest: {
        sourceEntity: activeEntityScope.entityKey,
        userKey: account.key,
        addRoleKeys,
        removeRoleKeys,
      },
    });
  };

  const renderOrganizationHeaderAction = () => {
    if (organizations.data?.length === 0) {
      return (
        <Text type="$bodyBase.default">
          {i18n.t('Page_RoleAssignments.noOrganizations')}
        </Text>
      );
    }

    return (
      <VStack flexGrow={1} maxWidth={'75%'}>
        <DropdownList
          items={organizations.data?.map((org) => org.name) ?? []}
          selectedIndex={selectedOrganizationIndex}
          onSelect={(index) => {
            if (index !== undefined) {
              setSelectedOrganizationIndex(index);
            }
          }}
        >
          <DropdownButton
            label={
              organizations.isPending
                ? i18n.t('Common.loading')
                : (currentOrganization?.name ?? '')
            }
            onPress={() => {}}
            size="medium"
            variant="secondary"
            disabled={organizations.isPending || isDisabledDueToLoading}
          />
        </DropdownList>
      </VStack>
    );
  };

  const renderStudyHeaderAction = () => {
    if (studies.data?.length === 0) {
      return (
        <Text type="$bodyBase.default">
          {i18n.t('Page_RoleAssignments.noStudies')}
        </Text>
      );
    }

    return (
      <VStack flexGrow={1} maxWidth={'75%'}>
        <DropdownList
          items={studies.data?.map((study) => study.name) ?? []}
          selectedIndex={selectedStudyIndex}
          onSelect={(index) => {
            if (index !== undefined) {
              setSelectedStudyIndex(index);
              setSelectedSiteIndex(0);
            }
          }}
        >
          <DropdownButton
            label={
              studies.isPending
                ? i18n.t('Common.loading')
                : (currentStudy?.name ?? '')
            }
            onPress={() => {}}
            size="medium"
            variant="secondary"
            disabled={studies.isPending || isDisabledDueToLoading}
          />
        </DropdownList>
      </VStack>
    );
  };

  const renderSiteHeaderAction = () => {
    if (!currentStudyKey || sites.data?.length === 0) {
      return (
        <VStack>
          <Text type="$bodyBase.default">
            {i18n.t('Page_RoleAssignments.noSites')}
          </Text>
        </VStack>
      );
    }

    return (
      <VStack flexGrow={1} maxWidth={'75%'}>
        <DropdownList
          items={sites.data?.map((site) => site.name) ?? []}
          selectedIndex={selectedSiteIndex}
          onSelect={(index) => {
            if (index !== undefined) {
              setSelectedSiteIndex(index);
            }
          }}
        >
          <DropdownButton
            label={
              sites.isPending
                ? i18n.t('Common.loading')
                : (currentSite?.name ?? '')
            }
            onPress={() => {}}
            size="medium"
            variant="secondary"
            disabled={sites.isPending || isDisabledDueToLoading}
          />
        </DropdownList>
      </VStack>
    );
  };

  const activeTab: TabConfigItem = (() => {
    switch (selectedTabIndex) {
      case TabIndex.Study:
        return {
          key: currentStudyKey,
          headerAction: renderStudyHeaderAction(),
        };
      case TabIndex.Site:
        return {
          key: currentSiteKey,
          headerAction: renderSiteHeaderAction(),
        };
      default:
        return {
          key: currentOrgKey,
          headerAction: renderOrganizationHeaderAction(),
        };
    }
  })();

  const renderEntityHierarchy = () => {
    if (!organizations.data?.length) {
      return null;
    }

    return (
      <HStack gap="$2" alignItems="center" testID="entity-hierarchy">
        <Text type="$labelBase.default" color="$text.80">
          {organizations.data?.[selectedOrganizationIndex]?.name}
        </Text>
        {selectedTabIndex >= TabIndex.Study && studies.data?.length ? (
          <>
            <Text type="$labelBase.default" color="$text.80">
              {'>'}
            </Text>
            <Text type="$labelBase.default" color="$text.80">
              {studies.data?.[selectedStudyIndex]?.name}
            </Text>
          </>
        ) : null}
        {selectedTabIndex >= TabIndex.Site && sites.data?.length ? (
          <>
            <Text type="$labelBase.default" color="$text.80">
              {'>'}
            </Text>
            <Text type="$labelBase.default" color="$text.80">
              {sites.data?.[selectedSiteIndex]?.name}
            </Text>
          </>
        ) : null}
      </HStack>
    );
  };

  return (
    <Card
      title={i18n.t('Page_RoleAssignments.title')}
      titleAriaLevel={1}
      titleTypeToken="$h4.strong"
    >
      <VStack gap="$3">
        {renderEntityHierarchy()}
        <Tabs
          tabs={tabs}
          selectedIndex={selectedTabIndex}
          onSelect={(index) => setSelectedTabIndex(index)}
          testID="account-roles-tabs"
        />
        {/* Update key when tab or dropdown changes to force table remount, resetting filters and selections intentionally */}
        <RoleAssignmentsTable
          key={`${selectedTabIndex}-${activeTab.key}`}
          accountsWithRoleAssignments={accountsWithRoleAssignments.data || []}
          roles={roles.data || []}
          isLoading={isLoadingAccounts || roles.isPending}
          isError={accountsWithRoleAssignments.isError || roles.isError}
          onSaveRoleChanges={onSaveRoleChanges}
          isSavingRoleChanges={updateRolesMutation.isPending}
          headerAction={activeTab.headerAction}
        />
      </VStack>
    </Card>
  );
}
